import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Heart, Star, MapPin, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProviderBadge } from '../components/ProviderBadge';

export default function Favourites() {
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchFavourites(); }, []);

  const fetchFavourites = async () => {
    try {
      const res = await api.get('/favourites');
      setFavourites(res.data || []);
    } catch { toast.error('Failed to load favourites'); }
    finally { setLoading(false); }
  };

  const removeFavourite = async (providerId) => {
    try {
      await api.delete(`/favourites/${providerId}`);
      setFavourites(prev => prev.filter(f => f.provider_id !== providerId));
      toast.success('Removed from favourites');
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
    </div>
  );

  return (
    <div data-testid="favourites-tab">
      {favourites.length === 0 ? (
        <div className="text-center py-16 card-surface" data-testid="favourites-empty">
          <Heart className="h-12 w-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg text-[#1A1A1A] mb-1">No favourites yet</h3>
          <p className="text-sm text-stone-500 mb-4">Browse providers and tap the heart icon to save them here.</p>
          <Link to="/discover" className="btn-primary inline-block px-6 py-2 rounded-lg">Browse providers</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="favourites-grid">
          {favourites.map(fav => (
            <div key={fav.provider_id} className="card-surface p-5 group hover:shadow-md transition-shadow" data-testid={`fav-${fav.provider_id}`}>
              <div className="flex items-start gap-4">
                <Link to={`/@${fav.username}`} className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center text-white text-xl font-heading overflow-hidden">
                    {fav.profile_image
                      ? <img src={fav.profile_image} alt={fav.brand_name} className="w-full h-full object-cover" />
                      : fav.brand_name?.charAt(0)}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/@${fav.username}`} className="hover:underline">
                    <h3 className="font-heading font-medium text-[#1A1A1A] truncate">{fav.brand_name}</h3>
                  </Link>
                  <p className="text-sm text-stone-500">@{fav.username}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {fav.rating > 0 && (
                      <span className="flex items-center gap-1 text-sm">
                        <Star className="h-3.5 w-3.5 text-[#D4AF37] fill-current" />
                        {fav.rating} ({fav.review_count})
                      </span>
                    )}
                    {fav.location && (
                      <span className="flex items-center gap-1 text-sm text-stone-400">
                        <MapPin className="h-3 w-3" />
                        {fav.location}
                      </span>
                    )}
                    <span className="text-xs text-stone-400">{fav.service_count} services</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {fav.badge && <ProviderBadge badge={fav.badge} size="xs" />}
                    {fav.top_rated && <ProviderBadge badge="top_rated" size="xs" />}
                  </div>
                </div>
                <button
                  onClick={() => removeFavourite(fav.provider_id)}
                  className="text-red-400 hover:text-red-600 transition-colors p-1"
                  data-testid={`remove-fav-${fav.provider_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
